import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  fileFormArrayToFormDataArray,
  getNewFileFormGroup,
} from '@app/helpers/form.helper';
import { FileUploadControl } from '@app/models/form.models';
import { BaseEnumResponse } from '@app/models/shared.models';
import { FileType } from '@app/models/shared.enums';
import { ComplainService } from '@app/services/complain.service';
import { GenericDataService } from '@app/services/generic-data.service';
import { ToastService } from '@app/services/toast.service';
import { CardComponent } from '@app/ui-components/card/card.component';
import { ButtonComponent } from '@app/ui-components/button/button.component';
import { UploadFilesComponent } from '@app/ui-components/upload-files/upload-files.component';
import { ValidatedDropdownComponent } from '@app/ui-components/validated-dropdown/validated-dropdown.component';
import { ValidatedTextAreaComponent } from '@app/ui-components/validated-textarea/validated-textarea.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';

type TicketKind = 'complaint' | 'message';

interface CreateTicketForm {
  complaintType: FormControl<number | null>;
  message: FormControl<string>;
  files: FormArray<FormGroup<FileUploadControl>>;
}

@Component({
  selector: 'app-create-ticket',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslatePipe,
    CardComponent,
    ButtonComponent,
    ValidatedDropdownComponent,
    ValidatedTextAreaComponent,
    UploadFilesComponent,
  ],
  templateUrl: './create-ticket.component.html',
  styleUrl: './create-ticket.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateTicketComponent implements OnInit {
  private static readonly messageComplaintTypeName = 'Other';

  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly complainService = inject(ComplainService);
  private readonly genericData = inject(GenericDataService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  private readonly ticketKind: TicketKind =
    (this.route.snapshot.data['ticketType'] as TicketKind) ?? 'message';

  readonly pageTitleKey = this.ticketKind === 'complaint' ? 'profile.complaint' : 'profile.message';

  readonly maxMessageLength = this.ticketKind === 'complaint' ? 5000 : 2500;

  readonly messageFieldLabelKey = signal(
    this.ticketKind === 'message'
      ? 'profile.createTicket.messageCustomerService'
      : 'profile.createTicket.complaintMessageFallback',
  );

  readonly isComplaintMode = this.ticketKind === 'complaint';

  readonly complaintTypes = signal<BaseEnumResponse[]>([]);
  readonly typesLoading = signal(false);
  readonly sending = signal(false);

  readonly form = this.fb.group<CreateTicketForm>({
    complaintType: this.fb.control<number | null>(null, [Validators.required]),
    message: this.fb.control<string>('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.maxLength(this.maxMessageLength),
      ],
    }),
    files: this.fb.nonNullable.array<FormGroup<FileUploadControl>>([
      getNewFileFormGroup(this.fb, FileType.document),
    ]),
  });

  ngOnInit(): void {
    this.typesLoading.set(true);
    this.genericData
      .loadContactComplaintTypes()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.typesLoading.set(false)),
      )
      .subscribe({
        next: (types) => this.applyLoadedComplaintTypes(types),
        error: () => {
          this.toast.error(
            this.translate.instant('profile.createTicket.typesLoadFailed'),
          );
        },
      });
  }

  addFileRow(): void {
    this.form.controls.files.push(getNewFileFormGroup(this.fb, FileType.document));
  }

  cancel(): void {
    const url = this.router.url;
    if (url.includes('/profile/legal')) {
      void this.router.navigate(['profile', 'legal', 'personalInfo.legalPerson']);
    } else {
      void this.router.navigate(['profile', 'natural', 'personal-info']);
    }
  }

  submit(): void {
    if (this.sending()) return;
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    const complaintType = this.form.controls.complaintType.value;
    if (complaintType == null) return;

    const filePayload = fileFormArrayToFormDataArray(this.form.controls.files);

    const formData = new FormData();
    formData.append('Message', this.form.controls.message.value);
    formData.append('ComplaintType', String(complaintType));
    filePayload.forEach((f) => {
      formData.append('UploadedFiles', f.uploadedFile);
      formData.append('UploadedFileDescriptions', f.uploadedFileDescription);
    });

    this.sending.set(true);
    this.complainService
      .createComplaint(formData)
      .pipe(finalize(() => this.sending.set(false)))
      .subscribe({
        next: () => {
          this.toast.success(
            this.translate.instant('profile.createTicket.sent'),
          );
          this.cancel();
        },
        error: () => {
          this.toast.error(
            this.translate.instant('profile.createTicket.sendFailed'),
          );
        },
      });
  }

  complaintTypeLabelKey(type: BaseEnumResponse): string {
    return `generic.contactComplaintType.${type.name}`;
  }

  onComplaintTypeSelected(): void {
    this.syncComplaintMessageLabelKey();
  }

  private applyLoadedComplaintTypes(types: BaseEnumResponse[]): void {
    this.complaintTypes.set(types);
    if (this.ticketKind === 'complaint') {
      const first = types[0];
      if (first !== undefined) {
        this.form.controls.complaintType.setValue(first.id);
      }
      this.syncComplaintMessageLabelKey();
      return;
    }
    const other = types.find(
      (t) => t.name === CreateTicketComponent.messageComplaintTypeName,
    );
    if (other !== undefined) {
      this.form.controls.complaintType.setValue(other.id);
    } else {
      this.toast.error(
        this.translate.instant('profile.createTicket.otherTypeMissing'),
      );
    }
  }

  private syncComplaintMessageLabelKey(): void {
    if (!this.isComplaintMode) return;
    const id = this.form.controls.complaintType.value;
    const row = this.complaintTypes().find((t) => t.id === id);
    if (row?.name) {
      this.messageFieldLabelKey.set(
        `generic.contactComplaintType.${row.name}`,
      );
    } else {
      this.messageFieldLabelKey.set(
        'profile.createTicket.complaintMessageFallback',
      );
    }
    this.cdr.markForCheck();
  }
}
